import React, { useEffect, useState, useCallback } from 'react';
import Loading from '../global/loading/Loading';
import { InPostPoint } from '../../models/InPostPoint';

// Używamy bardziej szczegółowej deklaracji typu
declare global {
  interface Window {
    InpostGeowidget?: any;
    easyPackAsyncInit?: () => void;
    onPoint?: any; // Dodajemy globalną funkcję callback
  }
  namespace JSX {
    interface IntrinsicElements {
      'inpost-geowidget': React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement>,
        HTMLElement
      > & { onpoint: string; token: string; language: string; config: string };
    }
  }
}

export interface InPostGeoWidgetProps {
  onPointSelect?: (point: InPostPoint | null) => void;
}

function InPostGeoWidget({ onPointSelect }: InPostGeoWidgetProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  // Token z zmiennych środowiskowych
  const token = process.env.REACT_APP_INPOST_GEOWIDGET_KEY?.trim();

  // Callback dla wyboru punktu - używamy useCallback jak w przykładzie
  const handlePointSelect = useCallback(
    async (event: any) => {
      if (!event) return;
      try {
        // const pointName = event.name;

        // Pobierz szczegółowe informacje o paczkomacie
        try {
          // const pointDetails = await getInPostPointByName(pointName);

          // Przekaż informacje o punkcie do komponentu nadrzędnego
          if (onPointSelect) {
            onPointSelect(event as InPostPoint);
          }
        } catch (fetchErr) {
          console.error('Failed to fetch point details:', fetchErr);
        }
      } catch (err) {
        console.error('Error handling point select event:', err);
      }
    },
    [onPointSelect],
  );

  // Przypinamy callback do window
  useEffect(() => {
    // Przypisanie funkcji callbacka do window.onPoint
    window.onPoint = handlePointSelect;

    // Czyszczenie po odmontowaniu
    return () => {
      window.onPoint = undefined;
    };
  }, [handlePointSelect]);

  // Funkcja do ładowania zewnętrznych zasobów (skrypt + css)
  useEffect(() => {
    if (!token) {
      setIsError(true);
      setIsLoading(false);
      return;
    }

    try {
      // Sprawdzamy czy skrypt już istnieje
      const existingScript = document.querySelector(
        'script[src="https://geowidget.inpost.pl/inpost-geowidget.js"]',
      );

      if (existingScript) {
        setIsLoading(false);
        return;
      }

      // Dodaj CSS - dodajemy do body jak w przykładzie
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://geowidget.inpost.pl/inpost-geowidget.css';
      link.type = 'text/css';
      document.body.appendChild(link);

      // Dodaj JS z callbackiem - również do body
      const script = document.createElement('script');
      script.src = 'https://geowidget.inpost.pl/inpost-geowidget.js';
      script.defer = true; // Tylko defer, bez async=false

      // Obsługa błędu ładowania
      script.onerror = () => {
        console.error('Failed to load InPost GeoWidget script');
        setIsError(true);
        setIsLoading(false);
      };

      // Oznaczenie zakończenia ładowania
      script.onload = () => {
        setIsLoading(false);
      };

      document.body.appendChild(script);
    } catch (err) {
      console.error('Error loading InPost GeoWidget resources:', err);
      setIsError(true);
      setIsLoading(false);
    }

    // Zamykamy timeout po 8 sekundach - ale już bez sprawdzania obecności widgetu
    const timeout = setTimeout(() => {
      setIsLoading(false);
    }, 8000);

    return () => {
      clearTimeout(timeout);
    };
  }, [token]);

  // Renderowanie komponentu w zależności od stanu
  if (isError || !token) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-red-400"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">
              Nie można załadować widgetu InPost
            </h3>
            <div className="mt-2 text-sm text-red-700">
              <p>
                {!token
                  ? 'Brak klucza API.'
                  : 'Wystąpił błąd podczas ładowania widgetu.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="h-96 flex items-center justify-center bg-gray-50 rounded-md border border-gray-200">
        <Loading />
      </div>
    );
  }

  // Renderowanie właściwego widgetu - używamy podejścia z działającego przykładu
  return (
    <div className="h-[500px] rounded-md overflow-hidden border border-gray-200">
      <inpost-geowidget
        token={token}
        onpoint="onPoint" // Zmiana z "onpointselect" na "onPoint"
        language="pl"
        config="parcelCollect" // Zmiana z "parcelcollect" na "parcelCollect"
        className="h-full w-full"
      />
    </div>
  );
}

export default InPostGeoWidget;
